import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "sic_cart_items";

function normalizePrice(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function normalizeQuantity(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1) return 1;
  return Math.floor(num);
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [cartReady, setCartReady] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setItems(parsed);
        }
      }
    } catch (error) {
      console.error("Gagal memuat cart dari localStorage:", error);
    } finally {
      setCartReady(true);
    }
  }, []);

  useEffect(() => {
    if (!cartReady) return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error("Gagal menyimpan cart:", error);
    }
  }, [items, cartReady]);

  const addToCart = useCallback((product, quantity = 1) => {
    if (!product || !product.id) {
      throw new Error("Produk tidak valid. Pastikan produk memiliki id.");
    }

    const safeQuantity = normalizeQuantity(quantity);

    setItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === product.id);

      if (existingIndex >= 0) {
        return prev.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity: item.quantity + safeQuantity,
              }
            : item
        );
      }

      return [
        ...prev,
        {
          id: product.id,
          slug: product.slug || "",
          name: product.name || "Produk Tanpa Nama",
          price: normalizePrice(product.price),
          image: product.image || "",
          sku: product.sku || "",
          stock: product.stock ?? null,
          quantity: safeQuantity,
        },
      ];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setItems((prev) => prev.filter((item) => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    const safeQuantity = normalizeQuantity(quantity);

    setItems((prev) =>
      prev.map((item) =>
        item.id === productId
          ? {
              ...item,
              quantity: safeQuantity,
            }
          : item
      )
    );
  }, []);

  const increaseQuantity = useCallback((productId) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === productId
          ? {
              ...item,
              quantity: item.quantity + 1,
            }
          : item
      )
    );
  }, []);

  const decreaseQuantity = useCallback((productId) => {
    setItems((prev) =>
      prev
        .map((item) =>
          item.id === productId
            ? {
                ...item,
                quantity: item.quantity - 1,
              }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback(
    (productId) => items.some((item) => item.id === productId),
    [items]
  );

  const getItemQuantity = useCallback(
    (productId) => items.find((item) => item.id === productId)?.quantity || 0,
    [items]
  );

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [items]);

  const totalItems = useMemo(() => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }, [items]);

  const totalUniqueItems = useMemo(() => items.length, [items]);

  const value = useMemo(
    () => ({
      cartReady,
      items,
      setItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      increaseQuantity,
      decreaseQuantity,
      clearCart,
      isInCart,
      getItemQuantity,
      subtotal,
      totalItems,
      totalUniqueItems,
    }),
    [
      cartReady,
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      increaseQuantity,
      decreaseQuantity,
      clearCart,
      isInCart,
      getItemQuantity,
      subtotal,
      totalItems,
      totalUniqueItems,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart harus digunakan di dalam CartProvider");
  }

  return context;
}